"use client";

import { BadgeCheck } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import ArrowIcon from "@/components/icons/arrow";
import { cn } from "@/lib/utils";
import type { BoardMember } from "@/types/dashboard";

const CARD_WIDTH = 288;
const GAP = 16;

const MemberCard = ({
  member,
  isActive,
}: {
  member: BoardMember;
  isActive: boolean;
}) => (
  <Link
    className={cn(
      "bg-white rounded-2xl p-6 flex flex-col gap-4 shrink-0 transition-transform duration-300 ease-out",
      isActive ? "scale-105" : "scale-95 opacity-60",
    )}
    href={`/bilim-kurulu/${member.id}/article`}
    key={member.id}
    style={{ width: CARD_WIDTH }}
  >
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-full overflow-hidden bg-neutral-200 shrink-0">
        {member.avatarUrl && (
          <Image
            alt={member.name}
            className="object-cover w-full h-full"
            height={48}
            src={member.avatarUrl}
            width={48}
          />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="font-bold text-base leading-tight truncate">
            {member.name}
          </span>
          {member.isVerified && (
            <BadgeCheck
              className="text-blue-500 shrink-0"
              fill="currentColor"
              size={18}
              stroke="white"
              strokeWidth={1.5}
            />
          )}
        </div>
        <p className="text-sm text-neutral-500 truncate">{member.title}</p>
      </div>
    </div>
    <p className="text-sm leading-relaxed line-clamp-5">{member.bio}</p>
  </Link>
);

export const BoardMemberCarousel = ({
  members,
}: {
  members: BoardMember[];
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(() => setContainerWidth(el.offsetWidth));
    ro.observe(el);
    setContainerWidth(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  const translateX = containerWidth
    ? (containerWidth - CARD_WIDTH) / 2 - currentIndex * (CARD_WIDTH + GAP)
    : 0;

  return (
    <div
      className="bg-neutral-100 h-full rounded-2xl flex flex-col justify-between py-8 gap-6 overflow-hidden"
      ref={containerRef}
    >
      <div
        className="flex transition-transform duration-300 ease-out"
        style={{ gap: GAP, transform: `translateX(${translateX}px)` }}
      >
        {members.map((m, i) => (
          <MemberCard isActive={i === currentIndex} key={m.id} member={m} />
        ))}
      </div>
      <div className="flex justify-end gap-2 px-6">
        <button
          className="w-10 h-10 rounded-full bg-white flex items-center justify-center disabled:opacity-30 cursor-pointer disabled:cursor-default transition-opacity"
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
          type="button"
        >
          <ArrowIcon className="stroke-brand-blue-600 rotate-180" />
        </button>
        <button
          className="w-10 h-10 rounded-full bg-white flex items-center justify-center disabled:opacity-30 cursor-pointer disabled:cursor-default transition-opacity"
          disabled={currentIndex === members.length - 1}
          onClick={() =>
            setCurrentIndex((i) => Math.min(members.length - 1, i + 1))
          }
          type="button"
        >
          <ArrowIcon className="stroke-brand-blue-600" />
        </button>
      </div>
    </div>
  );
};
